import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Phone, ChevronRight, Flame, Users, Utensils } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const CHEF_IMAGE = "https://customer-assets.emergentagent.com/job_bcf6ecb8-480f-48a0-b555-9c0d6414b56f/artifacts/sy3v37j1_Capture%20d%27%C3%A9cran%202026-02-19%20173007.png";
const EXTERIOR_IMAGE = "https://customer-assets.emergentagent.com/job_1bf35c11-3683-4ea1-b498-d0435b0fdb42/artifacts/i0vnrlsm_Capture%20d%27%C3%A9cran%202026-04-21%20213055.png";
const HERO_BG = "https://images.unsplash.com/photo-1769733340965-11a667dd592b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxpdGFsaWFuJTIwcmVzdGF1cmFudCUyMGNvbG9yZnVsJTIwcGl6emElMjBwYXN0YSUyMGZvb2QlMjB0YWJsZSUyMHdhcm18ZW58MHx8fHwxNzc2ODAwMzI5fDA&ixlib=rb-4.1.0&q=85";
const PIZZA_CLOSE = "https://images.pexels.com/photos/3343622/pexels-photo-3343622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// New food images from user
const SALADE_IMG = "https://customer-assets.emergentagent.com/job_1bf35c11-3683-4ea1-b498-d0435b0fdb42/artifacts/2t060ihs_IMG_6818.jpeg";
const ESCALOPE_IMG = "https://customer-assets.emergentagent.com/job_1bf35c11-3683-4ea1-b498-d0435b0fdb42/artifacts/hilovgxw_IMG_0133.jpeg";
const JAMBON_MELON_IMG = "https://customer-assets.emergentagent.com/job_1bf35c11-3683-4ea1-b498-d0435b0fdb42/artifacts/rx5jx9ql_IMG_0135.jpeg";

const Home = () => {
  useSEO({
    title: 'NotaBene Sympho — Pizzeria & Restaurant Italien à Mons',
    description: 'Restaurant italien à Mons (Chaussée du Roi Baudouin 161). Pizzas maison, pâtes fraîches, escalopes. Réservation, à emporter et livraison. Ouvert du mardi au dimanche.',
    canonical: '/',
  });
  return (
    <div className="pt-0" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[100vh] flex flex-col justify-center -mt-[112px] pt-[112px]" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col items-center justify-center flex-1">
          <div className="text-center mb-8">
            <div className="animate-fade-in">
              <p className="text-[#F59E0B] font-medium tracking-widest uppercase mb-4 text-sm sm:text-base">
                Restaurant Italien à Saint-Symphorien
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
                Nota Bene<br />
                <span className="text-[#F59E0B]">Sympho</span>
              </h1>
              <p className="text-white/90 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8">
                Pizzas cuites au feu de bois, pâtes maison et spécialités italiennes. 
                Une cuisine généreuse dans une ambiance chaleureuse.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                <Link to="/commander">
                  <Button
                    className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-semibold shadow-lg shadow-red-900/30 w-full sm:w-auto"
                    data-testid="hero-order-btn"
                  >
                    Commander maintenant
                  </Button>
                </Link>
                <Link to="/reservation">
                  <Button
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-[#1C1917] rounded-full px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-semibold w-full sm:w-auto"
                    data-testid="hero-reserve-btn"
                  >
                    Réserver une table
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Quick Info Bar - Inside the hero */}
          <div className="w-full max-w-4xl mx-auto mb-8">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 sm:p-5 shadow-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#C62E2E]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#C62E2E]" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-[#57534E] uppercase tracking-wide">Adresse</p>
                    <p className="font-semibold text-[#1C1917] text-sm">Chaussée du Roi Baudouin 161, 7030 Mons</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#C62E2E]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#C62E2E]" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-[#57534E] uppercase tracking-wide">Horaires</p>
                    <p className="font-semibold text-[#1C1917] text-sm">Mer-Dim: 12h-14h30 / 18h-22h</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#C62E2E]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#C62E2E]" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-[#57534E] uppercase tracking-wide">Réservation</p>
                    <a href="tel:+3265950649" className="font-semibold text-[#1C1917] text-sm hover:text-[#C62E2E] transition-colors">
                      065 95 06 49
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce z-10">
          <ChevronRight className="w-8 h-8 text-white/70 rotate-90" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-[#F5F2EA]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 rounded-full bg-[#C62E2E]/10 flex items-center justify-center mx-auto mb-6">
                <Flame className="w-8 h-8 text-[#C62E2E]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-3">Four à Bois</h3>
              <p className="text-[#57534E]">
                Nos pizzas sont cuites dans un authentique four à bois pour une pâte parfaitement croustillante.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 rounded-full bg-[#C62E2E]/10 flex items-center justify-center mx-auto mb-6">
                <Utensils className="w-8 h-8 text-[#C62E2E]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-3">Cuisine Maison</h3>
              <p className="text-[#57534E]">
                Tous nos plats sont préparés avec amour et des ingrédients frais sélectionnés avec soin.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 rounded-full bg-[#C62E2E]/10 flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-[#C62E2E]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-3">Ambiance Familiale</h3>
              <p className="text-[#57534E]">
                Un cadre chaleureux et convivial pour partager de bons moments en famille ou entre amis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Chef Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="chef-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <img
                src={CHEF_IMAGE}
                alt="Chef Ernesto"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#C62E2E] text-white rounded-2xl p-6 shadow-xl">
                <p className="font-['Playfair_Display'] text-3xl font-bold">20+</p>
                <p className="text-sm opacity-90">Années d'expérience</p>
              </div>
            </div>
            
            <div>
              <p className="text-[#C62E2E] font-medium tracking-widest uppercase mb-4">
                Notre Chef
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1C1917] mb-6">
                Ernesto, la passion de l'Italie
              </h2>
              <p className="text-[#57534E] text-lg leading-relaxed mb-6">
                Avec plus de 20 ans d'expérience, Ernesto apporte l'authenticité de la cuisine italienne 
                à chaque plat. Sa passion pour les ingrédients frais et les recettes traditionnelles 
                fait de NotaBene Sympho une destination incontournable pour les amateurs de vraie 
                cuisine italienne à Mons.
              </p>
              <p className="text-[#57534E] text-lg leading-relaxed mb-8">
                "Mon secret ? Des ingrédients simples mais de qualité, et beaucoup d'amour dans chaque pizza."
              </p>
              <Link to="/restaurant">
                <Button
                  variant="outline"
                  className="border-2 border-[#1C1917] text-[#1C1917] hover:bg-[#1C1917] hover:text-white rounded-full px-8 py-3"
                  data-testid="discover-restaurant-btn"
                >
                  Découvrir le restaurant
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Preview - Nos Spécialités */}
      <section className="py-20 md:py-32 bg-[#F5F2EA]" data-testid="menu-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C62E2E] font-medium tracking-widest uppercase mb-4">
              Nos Spécialités
            </p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1C1917] mb-6">
              La vraie cuisine italienne
            </h2>
            <p className="text-[#57534E] text-lg max-w-2xl mx-auto">
              Des plats généreux préparés avec passion, des entrées raffinées aux desserts gourmands.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={SALADE_IMG}
                  alt="Salade fraîche aux artichauts et champignons"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-2">Entrées & Salades</h3>
                <p className="text-[#57534E]">Salades fraîches, antipasti et bruschette</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group">
              <div className="aspect-[4/3] overflow-hidden relative">
                <img
                  src={ESCALOPE_IMG}
                  alt="Escalope sauce crème avec pâtes"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-4 right-4 bg-[#C62E2E] text-white px-3 py-1 rounded-full text-sm font-semibold">
                  MAISON
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-2">Plats & Escalopes</h3>
                <p className="text-[#57534E]">Escalopes, pizzas et pâtes préparées avec soin</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={JAMBON_MELON_IMG}
                  alt="Jambon de Parme et melon"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-2">Spécialités Italiennes</h3>
                <p className="text-[#57534E]">Jambon de Parme, charcuteries et fromages italiens</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/commander">
              <Button
                className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full px-10 py-6 text-lg font-semibold"
                data-testid="see-full-menu-btn"
              >
                Commander maintenant
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative py-20 md:py-32"
        style={{
          backgroundImage: `url(${EXTERIOR_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-[#1C1917]/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-6">
            Prêt à vous régaler ?
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
            Réservez votre table pour une soirée inoubliable ou commandez vos plats préférés 
            à emporter ou en livraison.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/reservation">
              <Button
                className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full px-10 py-6 text-lg font-semibold"
                data-testid="cta-reserve-btn"
              >
                Réserver une table
              </Button>
            </Link>
            <Link to="/commander">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#1C1917] rounded-full px-10 py-6 text-lg font-semibold"
                data-testid="cta-order-btn"
              >
                Commander à emporter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
